import { useState, useRef, useEffect, useCallback, forwardRef, type ReactNode } from 'react';
import { MapPin, Search, RefreshCw } from 'lucide-react';
import { Dialog } from '@/components/common/Dialog';
import { useI18n } from '@/context/I18nContext';
import { useToast } from '@/context/ToastContext';
import { rpc } from '@/api/client';

/* ── Google Maps type shims ───────────────────────────────────── */
declare global {
  interface Window {
    google?: typeof google;
  }
}

interface SessionTrack {
  id: string;
  displayName: string;
}

interface Props {
  open: boolean;
  editing: {
    id: string;
    name: string;
    latitude: string;
    longitude: string;
    zoom?: number;
    sessionTrackIds: string[];
    raceEventTrackNames: string[];
    hasImage?: boolean;
  } | null;
  saving: boolean;
  onSave: (values: {
    name: string;
    latitude: string;
    longitude: string;
    zoom: number;
    sessionTrackIds: string[];
    raceEventTrackNames: string[];
  }) => void;
  onClose: () => void;
}

interface Suggestion {
  placeId: string;
  description: string;
}

/* ── Tab types ────────────────────────────────────────────────── */
type TrackFormTab = 'location' | 'aliases';
const TABS: TrackFormTab[] = ['location', 'aliases'];

/* ── Section component (matches SetupForm pattern) ────────────── */
interface SectionProps {
  tab: TrackFormTab;
  label: string;
  children: ReactNode;
}

const Section = forwardRef<HTMLDivElement, SectionProps>(({ tab, label, children }, ref) => (
  <div
    ref={ref}
    data-section={tab}
    style={{ scrollMarginTop: '52px' }}
  >
    <h3 className="section-title mb-4">{label}</h3>
    {children}
  </div>
));
Section.displayName = 'Section';

const DEFAULT_CENTER = { lat: 47.35, lng: 8.28 }; // roughly central Switzerland
const DEFAULT_ZOOM = 6;
const PLACED_ZOOM = 15;

export function TrackFormDialog({ open, editing, saving, onSave, onClose }: Props) {
  const { t } = useI18n();
  const { showToast } = useToast();

  /* ── Form state ─────────────────────────────────────────────── */
  const [name, setName] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [zoom, setZoom] = useState<number>(0);

  /* ── Satellite Image state ──────────────────────────────────── */
  const [hasCachedImage, setHasCachedImage] = useState(false);
  const [fetchingImage, setFetchingImage] = useState(false);
  const [imageTimestamp, setImageTimestamp] = useState(Date.now());

  /* ── Alias state ─────────────────────────────────────────────── */
  const [sessionTracks, setSessionTracks] = useState<SessionTrack[]>([]);
  const [raceEventTrackNames, setRaceEventTrackNames] = useState<string[]>([]);
  const [selectedSessionTrackIds, setSelectedSessionTrackIds] = useState<string[]>([]);
  const [selectedRaceEventTrackNames, setSelectedRaceEventTrackNames] = useState<string[]>([]);

  /* ── Tab state ───────────────────────────────────────────────── */
  const [activeTab, setActiveTab] = useState<TrackFormTab>('location');
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const isScrollingTo = useRef(false);
  const isObserverUpdate = useRef(false);

  /* ── Autocomplete state ─────────────────────────────────────── */
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  /* ── Refs ────────────────────────────────────────────────────── */
  const mapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initDoneRef = useRef(false);
  const nameRef = useRef('');

  // Keep nameRef in sync so the map init closure can read it
  nameRef.current = name;

  /* ── Reset form when dialog opens ───────────────────────────── */
  useEffect(() => {
    if (!open) {
      initDoneRef.current = false;
      return;
    }
    setName(editing?.name ?? '');
    setLat(editing?.latitude != null ? String(editing.latitude) : '');
    setLng(editing?.longitude != null ? String(editing.longitude) : '');
    setZoom(editing?.zoom ?? 0);
    setHasCachedImage(!!editing?.hasImage);
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedSessionTrackIds(editing?.sessionTrackIds ?? []);
    setSelectedRaceEventTrackNames(editing?.raceEventTrackNames ?? []);
    setActiveTab('location');

    // Load alias options
    rpc<SessionTrack[]>('ListSessionTracks').then(setSessionTracks).catch(() => setSessionTracks([]));
    rpc<string[]>('ListRaceEventTrackNames').then(setRaceEventTrackNames).catch(() => setRaceEventTrackNames([]));
  }, [open, editing]);

  const handleFetchSatelliteImage = async () => {
    const trackId = editing?.id || name.trim().toLowerCase().replace(/\s+/g, '-');
    if (!trackId || !parseFloat(lat) || !parseFloat(lng)) return;
    setFetchingImage(true);
    try {
      await rpc('FetchRaceTrackImage', {
        id: trackId,
        latitude: parseFloat(lat) || 0,
        longitude: parseFloat(lng) || 0,
        zoom: zoom,
      });
      setHasCachedImage(true);
      setImageTimestamp(Date.now());
      showToast(t('tracks.imageFetched'));
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setFetchingImage(false);
    }
  };

  /* ── Tab click → scroll to section ──────────────────────────── */
  const handleTabChange = useCallback((tab: TrackFormTab) => {
    const el = sectionRefs.current[tab];
    if (el) {
      isScrollingTo.current = true;
      setActiveTab(tab);
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        isScrollingTo.current = false;
      }, 600);
    }
  }, []);

  /* ── Intersection observer — track visible section ──────────── */
  useEffect(() => {
    if (!open) return;

    // Small delay to let sections render
    const timeoutId = setTimeout(() => {
      const sections = TABS
        .map(tab => sectionRefs.current[tab])
        .filter((el): el is HTMLDivElement => el !== null);

      if (sections.length === 0) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (isScrollingTo.current) return;

          let topmost: { tab: TrackFormTab; top: number } | null = null;
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const tab = entry.target.getAttribute('data-section') as TrackFormTab;
              const top = entry.boundingClientRect.top;
              if (!topmost || top < topmost.top) {
                topmost = { tab, top };
              }
            }
          }

          if (topmost) {
            isObserverUpdate.current = true;
            setActiveTab(topmost.tab);
          }
        },
        {
          threshold: 0.1,
          rootMargin: '-80px 0px -60% 0px',
        },
      );

      sections.forEach(el => observer.observe(el));
      return () => observer.disconnect();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [open]);

  /* ── Place or move marker ───────────────────────────────────── */
  const placeMarker = useCallback((position: google.maps.LatLngLiteral) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (markerRef.current) {
      markerRef.current.setPosition(position);
    } else {
      markerRef.current = new google.maps.Marker({
        map,
        position,
        draggable: true,
      });

      // Update coords when marker is dragged
      markerRef.current.addListener('dragend', () => {
        const pos = markerRef.current?.getPosition();
        if (pos) {
          setLat(pos.lat().toFixed(6));
          setLng(pos.lng().toFixed(6));
        }
      });
    }
  }, []);

  /* ── Initialise map ─────────────────────────────────────────── */
  useEffect(() => {
    if (!open || initDoneRef.current) return;

    const init = () => {
      if (!mapRef.current || !window.google) return;

      const hasCoords = editing?.latitude && editing?.longitude;
      const center = hasCoords
        ? { lat: parseFloat(editing.latitude), lng: parseFloat(editing.longitude) }
        : DEFAULT_CENTER;
      const zoom = hasCoords ? PLACED_ZOOM : DEFAULT_ZOOM;

      // Create map with satellite/hybrid view
      const map = new google.maps.Map(mapRef.current, {
        center,
        zoom,
        mapTypeId: 'hybrid',
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'greedy',
      });
      mapInstanceRef.current = map;

      // Autocomplete + Places services
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
      placesServiceRef.current = new google.maps.places.PlacesService(map);

      // Place marker if editing existing track
      if (hasCoords) {
        placeMarker(center);
      }

      // Click-to-place / adjust marker
      map.addListener('click', (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;
        const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        placeMarker(pos);
        setLat(pos.lat.toFixed(6));
        setLng(pos.lng.toFixed(6));
      });

      initDoneRef.current = true;
    };

    // Google Maps may still be loading
    if (window.google?.maps) {
      requestAnimationFrame(init);
    } else {
      const check = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(check);
          requestAnimationFrame(init);
        }
      }, 200);
      return () => clearInterval(check);
    }
  }, [open, editing, placeMarker]);

  /* ── Fetch autocomplete suggestions ─────────────────────────── */
  const fetchSuggestions = useCallback((query: string) => {
    if (!autocompleteServiceRef.current || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    autocompleteServiceRef.current.getPlacePredictions(
      { input: query },
      (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(
            predictions.map((p) => ({
              placeId: p.place_id,
              description: p.description,
            }))
          );
          setShowSuggestions(true);
          setActiveIndex(-1);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }
    );
  }, []);

  /* ── Handle search input ────────────────────────────────────── */
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 250);
  };

  /* ── Select a suggestion ────────────────────────────────────── */
  const selectSuggestion = useCallback((placeId: string) => {
    if (!placesServiceRef.current) return;

    placesServiceRef.current.getDetails(
      { placeId, fields: ['geometry', 'name'] },
      (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.geometry?.location) return;

        const pos = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };

        const map = mapInstanceRef.current;
        if (map) {
          map.panTo(pos);
          map.setZoom(PLACED_ZOOM);
        }
        placeMarker(pos);
        setLat(pos.lat.toFixed(6));
        setLng(pos.lng.toFixed(6));

        // Auto-fill track name if empty
        if (!nameRef.current && place.name) {
          setName(place.name);
        }

        setShowSuggestions(false);
        setSearchQuery('');
      }
    );
  }, [placeMarker]);

  /* ── Keyboard navigation for suggestions ────────────────────── */
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[activeIndex].placeId);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  /* ── Close suggestions on outside click ─────────────────────── */
  useEffect(() => {
    if (!showSuggestions) return;
    const handler = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        searchRef.current &&
        !searchRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSuggestions]);

  /* ── Clean up on unmount ────────────────────────────────────── */
  useEffect(() => {
    return () => {
      markerRef.current = null;
      mapInstanceRef.current = null;
      autocompleteServiceRef.current = null;
      placesServiceRef.current = null;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  /* ── Alias toggle helpers ─────────────────────────────────── */
  const toggleSessionTrack = (id: string) => {
    setSelectedSessionTrackIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleRaceEventTrackName = (trackName: string) => {
    setSelectedRaceEventTrackNames((prev) =>
      prev.includes(trackName) ? prev.filter((x) => x !== trackName) : [...prev, trackName]
    );
  };

  /* ── Section ref setter ─────────────────────────────────────── */
  const setRef = useCallback((tab: TrackFormTab) => (el: HTMLDivElement | null) => {
    sectionRefs.current[tab] = el;
  }, []);

  /* ── Submit handler ─────────────────────────────────────────── */
  const handleSubmit = () => {
    onSave({
      name: name.trim(),
      latitude: lat.trim(),
      longitude: lng.trim(),
      zoom,
      sessionTrackIds: selectedSessionTrackIds,
      raceEventTrackNames: selectedRaceEventTrackNames,
    });
  };

  /* ── Tab bar (matches SubTabBar style) ──────────────────────── */
  const tabBar = (
    <div
      className="flex gap-1 pb-0 overflow-x-auto"
      role="tablist"
      style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
    >
      {TABS.map(tab => (
        <button
          key={tab}
          role="tab"
          aria-selected={activeTab === tab}
          onClick={() => handleTabChange(tab)}
          className={`
            bg-none border-none text-xs px-3.5 py-2 cursor-pointer shrink-0 whitespace-nowrap
            border-b-2 -mb-px rounded-t-sm
            transition-colors duration-150
            ${activeTab === tab
              ? 'text-accent border-accent font-semibold'
              : 'text-text-muted border-transparent hover:text-text-primary hover:bg-bg-hover'
            }
          `}
        >
          {tab === 'location' ? t('tracks.tabLocation') : t('tracks.tabAliases')}
        </button>
      ))}
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t('crud.editEntity', { entity: t('entity.track') }) : t('crud.newEntity', { entity: t('entity.track') })}
      stickyContent={tabBar}
      footer={
        <>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white
              hover:bg-accent-hover disabled:opacity-50 transition-colors cursor-pointer"
          >
            {editing ? t('common.save') : t('common.create')}
          </button>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-text-secondary
              border border-border-default hover:border-border-hover hover:text-text-primary transition-colors cursor-pointer"
          >
            {t('common.cancel')}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-8">
        {/* ── Location Section ──────────────────────────────────── */}
        <Section ref={setRef('location')} tab="location" label={t('tracks.tabLocation')}>
          <div className="space-y-4">
            {/* Track Name */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-text-muted">{t('tracks.name')}</label>
              <input
                type="text"
                placeholder="e.g. Wohlen"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
                className="editor-input"
                autoFocus
              />
            </div>

            {/* Address Search */}
            <div className="flex flex-col gap-1.5 relative">
              <label className="text-xs font-medium text-text-muted flex items-center gap-1.5">
                <Search size={12} />
                {t('tracks.searchAddress')}
              </label>
              <input
                ref={searchRef}
                type="text"
                placeholder={t('tracks.searchAddress')}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                autoComplete="off"
                className="editor-input"
              />

              {/* Custom suggestions dropdown (rendered inside dialog DOM) */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  className="absolute left-0 right-0 top-full mt-1 z-50
                    bg-bg-raised border border-border-subtle rounded-lg shadow-lg
                    overflow-hidden max-h-[200px] overflow-y-auto"
                >
                  {suggestions.map((s, i) => (
                    <button
                      key={s.placeId}
                      type="button"
                      onClick={() => selectSuggestion(s.placeId)}
                      className={`w-full text-left px-3 py-2.5 text-sm cursor-pointer
                        transition-colors flex items-center gap-2
                        ${i === activeIndex
                          ? 'bg-accent/15 text-text-primary'
                          : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary'
                        }
                        ${i < suggestions.length - 1 ? 'border-b border-border-subtle' : ''}`}
                    >
                      <MapPin size={13} className="shrink-0 text-text-muted" />
                      <span className="truncate">{s.description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Map & Coordinates */}
            <div className="flex flex-col gap-1.5">
              <div
                ref={mapRef}
                className="w-full rounded-lg border border-border-subtle overflow-hidden"
                style={{ height: '280px' }}
              />
              <div className="flex justify-between items-start text-[0.65rem] text-text-muted">
                <p className="flex items-center gap-1">
                  <MapPin size={10} />
                  {t('tracks.clickToAdjust')}
                </p>
                <div className="text-right tabular-nums">
                  {lat ? `${lat}, ${lng}` : '—'}
                </div>
              </div>
            </div>

            {/* Satellite Image Preview & Controls */}
            <div className="flex flex-col gap-3 pt-3 border-t border-border-subtle">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-text-primary">{t('tracks.satelliteImage')}</span>
                  {hasCachedImage && (
                    <span className="text-[0.65rem] px-1.5 py-0.5 rounded bg-accent/15 text-accent font-medium">
                      {zoom > 0 ? `Z${zoom}` : t('tracks.autoZoom')}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleFetchSatelliteImage}
                  disabled={fetchingImage || !parseFloat(lat) || !parseFloat(lng)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-bg-surface border border-border-default hover:border-border-hover text-text-secondary hover:text-text-primary transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <RefreshCw size={12} className={fetchingImage ? 'animate-spin text-accent' : ''} />
                  {fetchingImage ? t('tracks.fetchingImage') : hasCachedImage ? t('tracks.refetchImage') : t('tracks.fetchImage')}
                </button>
              </div>

              {/* Controls for Zoom */}
              <div className="p-3 rounded-lg bg-bg-raised border border-border-subtle text-xs">
                {/* Zoom Control */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label className="font-medium text-text-muted">{t('tracks.imageZoom')}</label>
                    <span className="text-text-primary font-semibold tabular-nums">
                      {zoom === 0 ? t('tracks.autoZoom') : `Zoom ${zoom}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setZoom(0)}
                      className={`px-2 py-1 rounded text-[0.7rem] font-medium border transition-colors cursor-pointer ${
                        zoom === 0
                          ? 'bg-accent text-accent-fg border-accent'
                          : 'bg-bg-surface text-text-secondary border-border-default hover:border-border-hover'
                      }`}
                    >
                      Auto
                    </button>
                    <input
                      type="range"
                      min="15"
                      max="20"
                      step="1"
                      value={zoom === 0 ? 18 : zoom}
                      onChange={(e) => setZoom(parseInt(e.target.value, 10))}
                      className="flex-1 accent-accent h-1.5 bg-bg-surface rounded-lg cursor-pointer"
                    />
                    <span className="w-5 text-right font-mono tabular-nums text-text-muted">
                      {zoom === 0 ? '18*' : zoom}
                    </span>
                  </div>
                </div>
              </div>

              {hasCachedImage && (
                <div className="relative w-full aspect-square rounded-lg border border-border-subtle overflow-hidden bg-bg-surface flex items-center justify-center">
                  <img
                    src={`/rpc/GetTrackImage?trackId=${encodeURIComponent(editing?.id || name.trim().toLowerCase().replace(/\s+/g, '-'))}&t=${imageTimestamp}`}
                    alt="Satellite Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        </Section>

        {/* ── Aliases Section ──────────────────────────────────── */}
        <Section ref={setRef('aliases')} tab="aliases" label={t('tracks.tabAliases')}>
          <div className="space-y-4">
            {/* Session Tracks */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-text-muted">{t('tracks.sessionTracks')}</label>
              {sessionTracks.length === 0 ? (
                <p className="text-xs text-text-muted italic">{t('tracks.noSessionTracks')}</p>
              ) : (
                <div className="max-h-[140px] overflow-y-auto rounded-lg border border-border-subtle bg-bg-raised p-2 space-y-1">
                  {sessionTracks.map((st) => (
                    <label key={st.id} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-bg-hover transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedSessionTrackIds.includes(st.id)}
                        onChange={() => toggleSessionTrack(st.id)}
                        className="accent-accent"
                      />
                      <span className="text-sm text-text-primary truncate">{st.displayName}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Race Event Tracks */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-text-muted">{t('tracks.raceEventTracks')}</label>
              {raceEventTrackNames.length === 0 ? (
                <p className="text-xs text-text-muted italic">{t('tracks.noRaceEventTracks')}</p>
              ) : (
                <div className="max-h-[140px] overflow-y-auto rounded-lg border border-border-subtle bg-bg-raised p-2 space-y-1">
                  {raceEventTrackNames.map((trackName) => (
                    <label key={trackName} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-bg-hover transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedRaceEventTrackNames.includes(trackName)}
                        onChange={() => toggleRaceEventTrackName(trackName)}
                        className="accent-accent"
                      />
                      <span className="text-sm text-text-primary truncate">{trackName}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Section>
      </div>
    </Dialog>
  );
}
